import { type ComponentType, useLayoutEffect, useState } from 'react';
import React from 'react';

export function useViewport() {
  // Initialize with undefined to prevent hydration mismatch
  const [isMobile, setIsMobile] = useState<boolean | undefined>(undefined);
  const [isPWA, setIsPWA] = useState<boolean | undefined>(undefined);
  const [isClient, setIsClient] = useState(false);
  useLayoutEffect(() => {
    setIsClient(true);

    const checkViewport = () => {
      setIsMobile(window.innerWidth < 768);
    };

    const checkPWA = () => {
      // Check if running as installed PWA
      const isStandalone = window.matchMedia(
        '(display-mode: standalone)',
      ).matches;
      const isFullscreen = window.matchMedia(
        '(display-mode: fullscreen)',
      ).matches;
      const hasManifest =
        document.querySelector('link[rel="manifest"]') !== null;

      // Additional PWA checks
      const isPWAInstalled =
        ('standalone' in window.navigator && window.navigator.standalone) || // iOS
        isStandalone ||
        isFullscreen ||
        (window.matchMedia('(display-mode: minimal-ui)').matches &&
          hasManifest);

      setIsPWA(!!isPWAInstalled);
    };

    // Initial checks
    checkViewport();
    checkPWA();

    // Add event listeners
    window.addEventListener('resize', checkViewport);
    window.matchMedia('(display-mode: standalone)').addListener(checkPWA);

    // Cleanup
    return () => {
      window.removeEventListener('resize', checkViewport);
      window.matchMedia('(display-mode: standalone)').removeListener(checkPWA);
    };
  }, []);

  // If we're on the server or haven't measured yet, assume defaults
  const defaultIsMobile = isMobile ?? false;
  const defaultIsPWA = isPWA ?? false;

  return {
    isMobile: defaultIsMobile,
    isPWA: defaultIsPWA,
    isClient,
  };
}

// Types for the viewport props
export interface ViewportProps {
  isMobile: boolean;
  isPWA: boolean;
  isClient: boolean;
}

// Higher-Order Component to inject viewport props
export function withViewport<P extends ViewportProps>(
  Component: ComponentType<P>,
) {
  const WithViewport = (props: Omit<P, keyof ViewportProps>) => {
    const viewport = useViewport();
    return React.createElement(Component, { ...viewport, ...(props as any) });
  };
  return WithViewport;
}

// Helper function to get CSS class based on viewport
export function getResponsiveClass(
  { isClient, isMobile }: ViewportProps,
  desktopClass: string,
  mobileClass: string,
): string {
  if (!isClient) {
    // During SSR, use mobile class as fallback to avoid hydration mismatch
    return mobileClass;
  }
  return isMobile ? mobileClass : desktopClass;
}
